// =============================================================================
// File: frontend/src/components/WhatCanICook.jsx
// "What Can I Cook?" — Pantry-first recipe discovery
// Patent claims exercised: P-002 Claims 3-5 (inventory-aware meal suggestions)
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';

import { useEscapeToClose } from '../hooks/useEscapeToClose';
const WhatCanICook = ({ token, fetchWithAuth, onClose, onBack, onViewRecipe }) => {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const isHubMode = !!onBack;
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pantryCount, setPantryCount] = useState(0);
  const [totalRecipes, setTotalRecipes] = useState(0);
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, ready, partial
  const [expandedId, setExpandedId] = useState(null);

  // Support both fetchWithAuth (modal mode from App.jsx) and raw token (hub mode)
  const authFetch = useCallback((url) => {
    if (fetchWithAuth) return fetchWithAuth(url);
    return fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  }, [fetchWithAuth, token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/pantry/what-can-i-cook`);
      if (!res.ok) throw new Error('Failed to load recipes');
      const data = await res.json();
      setRecipes(data.recipes || []);
      setPantryCount(data.pantry_count || 0);
      setTotalRecipes(data.total_recipes || 0);
      setMessage(data.message || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, API_BASE]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = recipes.filter(r => {
    if (filter === 'ready') return r.coverage_pct >= 80;
    if (filter === 'partial') return r.coverage_pct >= 40 && r.coverage_pct < 80;
    return true;
  });

  const readyCount = recipes.filter(r => r.coverage_pct >= 80).length;
  const partialCount = recipes.filter(r => r.coverage_pct >= 40 && r.coverage_pct < 80).length;

  const getCoverageColor = (pct) => {
    if (pct >= 80) return 'var(--brand-primary)';
    if (pct >= 50) return 'var(--brand-accent)';
    return 'var(--color-text-muted)';
  };

  const getCoverageBadge = (pct) => {
    if (pct === 100) return { label: 'Ready!', cls: 'wci-badge--ready' };
    if (pct >= 80) return { label: 'Almost', cls: 'wci-badge--almost' };
    if (pct >= 50) return { label: 'Partial', cls: 'wci-badge--partial' };
    return { label: 'Need More', cls: 'wci-badge--low' };
  };

  const content = (
    <>
          {/* Summary Bar */}
          <div className="wci-summaryBar">
            <div className="wci-stat">
              <span className="wci-statValue">{pantryCount}</span>
              <span className="wci-statLabel">Pantry Items</span>
            </div>
            <div className="wci-stat">
              <span className="wci-statValue">{totalRecipes}</span>
              <span className="wci-statLabel">Recipes Checked</span>
            </div>
            <div className="wci-stat wci-stat--highlight">
              <span className="wci-statValue">{readyCount}</span>
              <span className="wci-statLabel">Ready to Cook</span>
            </div>
          </div>

          {/* Filter Pills */}
          <div className="wci-filters">
            <button
              className={`wci-filterBtn ${filter === 'all' ? 'wci-filterBtn--active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({totalRecipes})
            </button>
            <button
              className={`wci-filterBtn ${filter === 'ready' ? 'wci-filterBtn--active' : ''}`}
              onClick={() => setFilter('ready')}
            >
              Ready ({readyCount})
            </button>
            <button
              className={`wci-filterBtn ${filter === 'partial' ? 'wci-filterBtn--active' : ''}`}
              onClick={() => setFilter('partial')}
            >
              Partial ({partialCount})
            </button>
          </div>

          {loading && (
            <div className="wci-loading">
              <div className="spinner"></div>
              <p>Scanning pantry against recipes...</p>
            </div>
          )}

          {error && (
            <div className="wci-error">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={loadData}>Retry</button>
            </div>
          )}

          {message && !loading && (
            <div className="wci-emptyState">
              <div className="wci-emptyIcon">🍳</div>
              <p>{message}</p>
            </div>
          )}

          {!loading && !error && !message && filtered.length === 0 && (
            <div className="wci-emptyState">
              <div className="wci-emptyIcon">🔍</div>
              <p>No recipes match this filter. Try "All" to see everything.</p>
            </div>
          )}

          {/* Recipe Cards */}
          {!loading && !error && filtered.length > 0 && (
            <div className="wci-recipeList">
              {filtered.map(recipe => {
                const badge = getCoverageBadge(recipe.coverage_pct);
                const isExpanded = expandedId === recipe.id;
                return (
                  <div key={recipe.id} className="wci-recipeCard">
                    <div className="wci-recipeMain" onClick={() => setExpandedId(isExpanded ? null : recipe.id)}>
                      <div className="wci-recipeInfo">
                        <div className="wci-recipeTitleRow">
                          <h4 className="wci-recipeTitle">{recipe.title}</h4>
                          {recipe.is_favorite && <span className="wci-favStar" title="Favorite">★</span>}
                          {/* #78B — saved recipe re-checked against CURRENT UHC; flag if now contraindicated */}
                          {recipe.uhc_flag && (
                            <span
                              title={(recipe.uhc_flag.reasons || []).map(r => r.guidance || r.mechanism).filter(Boolean).join(' · ') || 'Review against your current health context'}
                              style={{
                                marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                                whiteSpace: 'nowrap',
                                color: recipe.uhc_flag.severity === 'avoid' ? '#7f1d1d' : '#78350f',
                                background: recipe.uhc_flag.severity === 'avoid' ? '#fee2e2' : '#fef3c7',
                                border: `1px solid ${recipe.uhc_flag.severity === 'avoid' ? '#fca5a5' : '#fcd34d'}`,
                              }}
                            >
                              {recipe.uhc_flag.severity === 'avoid' ? '🚫 Not for you now' : '⚠️ Check with your health context'}
                            </span>
                          )}
                        </div>
                        <div className="wci-recipeMeta">
                          {recipe.category && <span>{recipe.category}</span>}
                          {recipe.cuisine_type && <span>{recipe.cuisine_type}</span>}
                          {recipe.prep_time_minutes && <span>{recipe.prep_time_minutes + (recipe.cook_time_minutes || 0)} min</span>}
                          {recipe.servings && <span>{recipe.servings} servings</span>}
                          {recipe.calories_per_serving && <span>{recipe.calories_per_serving} cal</span>}
                        </div>
                      </div>
                      <div className="wci-recipeCoverage">
                        <div className="wci-coverageCircle" style={{ borderColor: getCoverageColor(recipe.coverage_pct) }}>
                          <span className="wci-coveragePct">{Math.round(recipe.coverage_pct)}%</span>
                        </div>
                        <span className={`wci-badge ${badge.cls}`}>{badge.label}</span>
                        <span className="wci-ingredientCount">
                          {recipe.matched_count}/{recipe.total_ingredients}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="wci-recipeExpanded">
                        {recipe.matched_items.length > 0 && (
                          <div className="wci-ingredientSection">
                            <h5 className="wci-sectionLabel wci-sectionLabel--have">In Pantry</h5>
                            <div className="wci-ingredientTags">
                              {recipe.matched_items.map((item, i) => (
                                <span key={i} className="wci-tag wci-tag--have">{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {recipe.missing_items.length > 0 && (
                          <div className="wci-ingredientSection">
                            <h5 className="wci-sectionLabel wci-sectionLabel--need">Need to Buy</h5>
                            <div className="wci-ingredientTags">
                              {recipe.missing_items.map((item, i) => (
                                <span key={i} className="wci-tag wci-tag--need">{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="wci-recipeActions">
                          {onViewRecipe && (
                            <button className="btn btn-primary btn-sm" onClick={() => onViewRecipe({ id: recipe.id, title: recipe.title })}>
                              View Recipe
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
    </>
  );

  // Hub mode: render as page content (no modal overlay)
  if (isHubMode) {
    return (
      <div className="wci-hubWrapper">
        <div className="wci-hubHeader">
          <button className="btn btn-secondary btn-sm" onClick={onBack}>Back</button>
          <h2 className="wci-hubTitle">What Can I Cook?</h2>
        </div>
        {content}
      </div>
    );
  }

  // Modal mode: standard modal wrapper
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content modal-content-lg">
        <div className="modal-header">
          <h2>What Can I Cook?</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {content}
        </div>
      </div>
    </div>
  );
};

export default WhatCanICook;
